import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiCall } from '@/utils/api';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Filter,
  Calendar,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import LeadDetailDialog from '@/components/kommo/LeadDetailDialog';

interface LeadsData {
  leads_time_series: Array<{ period: string; count: number }>;
  granularity: 'day' | 'week' | 'month';
  leads_per_seller: Array<{ seller_id: number; seller_name: string; count: number }>;
  leads_per_status: Array<{ status_id: number; status_name: string | null; count: number }>;
  tag_breakdown: Array<{ tag_name: string; lead_count: number }>;
  total_leads: number;
  avg_per_day: number;
  status_map: Record<string, string>;
}

interface LeadItem {
  kommo_id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  price: string | null;
  status_id: number;
  status_name: string | null;
  seller_name: string;
  is_deleted: boolean;
  kommo_created_at: string;
  tags: string[];
}

interface LeadsListData {
  items: LeadItem[];
  total: number;
  page: number;
  per_page: number;
  last_page: number;
}

const PERIODS = [
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
  { value: '6m', label: '6 mois' },
  { value: '1y', label: '1 an' },
  { value: 'all', label: 'Tout' },
];

const GRANULARITY_LABELS: Record<string, string> = {
  day: 'Leads crees par jour',
  week: 'Leads crees par semaine',
  month: 'Leads crees par mois',
};

const KommoLeads: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<LeadsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('90d');
  const [sellerId, setSellerId] = useState<string>('');
  const [sellers, setSellers] = useState<Array<{ seller_id: number; seller_name: string }>>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Leads list state
  const [listData, setListData] = useState<LeadsListData | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listSearch, setListSearch] = useState('');
  const [listStatusId, setListStatusId] = useState<string>('');
  const [listTag, setListTag] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // Lead detail dialog
  const [dialogLeadId, setDialogLeadId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (sellerId) params.set('seller_id', sellerId);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);

      const res = await apiCall<{ success: boolean; data: LeadsData }>(
        token,
        `/kommo/analytics/leads?${params.toString()}`
      );

      if (res.success) {
        setData(res.data);
        if (!sellerId && res.data.leads_per_seller) {
          setSellers(res.data.leads_per_seller.map((s) => ({
            seller_id: s.seller_id,
            seller_name: s.seller_name,
          })));
        }
      }
    } catch (err) {
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  }, [token, period, sellerId, fromDate, toDate]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams({ period, page: String(listPage), per_page: '25' });
      if (sellerId) params.set('seller_id', sellerId);
      if (listStatusId) params.set('status_id', listStatusId);
      if (listTag) params.set('tag', listTag);
      if (listSearch) params.set('search', listSearch);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      params.set('sort', sortOrder);

      const res = await apiCall<{ success: boolean; data: LeadsListData }>(
        token,
        `/kommo/analytics/leads/list?${params.toString()}`
      );

      if (res.success) setListData(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la liste');
    } finally {
      setListLoading(false);
    }
  }, [token, period, sellerId, listPage, listStatusId, listTag, listSearch, fromDate, toDate, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data) fetchList();
  }, [fetchList, data]);

  useEffect(() => {
    setListPage(1);
  }, [period, sellerId, listStatusId, listTag, listSearch, fromDate, toDate]);

  const openLeadDialog = (kommoId: number) => {
    setDialogLeadId(kommoId);
    setDialogOpen(true);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setListPage(1);
    fetchList();
  };

  return (
    <div className="px-4 py-4 space-y-6">
      {/* Lead detail dialog */}
      <LeadDetailDialog
        kommoId={dialogLeadId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-5 rounded-xl border border-slate-200">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            Analyse des Leads
          </h1>
          <p className="text-sm text-slate-500 font-medium mt-0.5">
            {data ? `${data.total_leads.toLocaleString('fr-FR')} leads` : 'Chargement...'}
            {data ? ` | ${data.avg_per_day} leads/jour en moyenne` : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtres:</span>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sellerId || 'all'} onValueChange={(v) => setSellerId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tous les commerciaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les commerciaux</SelectItem>
                {sellers.map((s) => (
                  <SelectItem key={s.seller_id} value={String(s.seller_id)}>
                    {s.seller_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Du</span>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-[150px] h-9"
              />
              <span className="text-xs text-slate-400">au</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-[150px] h-9"
              />
              {(fromDate || toDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={() => { setFromDate(''); setToDate(''); }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : data ? (
        <>
          {/* Leads time series chart */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-bold text-slate-900">
                {GRANULARITY_LABELS[data.granularity] || 'Leads crees'}
              </CardTitle>
              <CardDescription className="text-xs text-slate-500">Evolution sur la periode selectionnee</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.leads_time_series}>
                    <defs>
                      <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                    <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#colorLeads)" name="Leads" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Leads list */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-bold text-slate-900">Liste des leads</CardTitle>
                  <CardDescription className="text-xs text-slate-500">
                    {listData ? `${listData.total.toLocaleString('fr-FR')} leads` : 'Chargement...'}
                  </CardDescription>
                </div>
                <form onSubmit={handleSearchSubmit} className="flex gap-2">
                  <Input
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="w-40"
                  />
                  <Button type="submit" variant="outline" size="sm">
                    <Search className="h-3.5 w-3.5" />
                  </Button>
                </form>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {data.leads_per_status.length > 0 && (
                  <Select value={listStatusId || 'all'} onValueChange={(v) => setListStatusId(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      {data.leads_per_status.map((s) => (
                        <SelectItem key={s.status_id} value={String(s.status_id)}>
                          {s.status_name || `#${s.status_id}`} ({s.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {data.tag_breakdown.length > 0 && (
                  <Select value={listTag || 'all'} onValueChange={(v) => setListTag(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue placeholder="Tous les tags" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les tags</SelectItem>
                      {data.tag_breakdown.slice(0, 30).map((t) => (
                        <SelectItem key={t.tag_name} value={t.tag_name}>
                          {t.tag_name} ({t.lead_count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {listLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : listData ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[60px]">ID</TableHead>
                          <TableHead>Lead / Contact</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Commercial</TableHead>
                          <TableHead>
                            <button
                              className="flex items-center gap-1 hover:text-slate-900 transition-colors"
                              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                            >
                              Date
                              {sortOrder === 'desc' ? (
                                <ArrowDown className="h-3 w-3" />
                              ) : (
                                <ArrowUp className="h-3 w-3" />
                              )}
                            </button>
                          </TableHead>
                          <TableHead>Tags</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {listData.items.map((lead) => (
                          <TableRow
                            key={lead.kommo_id}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => openLeadDialog(lead.kommo_id)}
                          >
                            <TableCell className="font-mono text-xs text-primary">
                              {lead.kommo_id}
                            </TableCell>
                            <TableCell>
                              <div>
                                <span className="text-sm font-medium text-slate-900">
                                  {lead.contact_name || lead.name || 'Sans nom'}
                                </span>
                                {lead.contact_name && lead.name && lead.contact_name !== lead.name && (
                                  <p className="text-[10px] text-slate-400">{lead.name}</p>
                                )}
                                {lead.contact_email && (
                                  <p className="text-[10px] text-slate-400">{lead.contact_email}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={lead.is_deleted ? 'destructive' : 'secondary'} className="text-[10px]">
                                {lead.is_deleted ? 'Supprime' : lead.status_name || `#${lead.status_id}`}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{lead.seller_name}</TableCell>
                            <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                              {new Date(lead.kommo_created_at).toLocaleDateString('fr-FR', { dateStyle: 'short' })}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1 max-w-[200px]">
                                {lead.tags.slice(0, 3).map((t) => (
                                  <Badge key={t} variant="outline" className="text-[9px] px-1.5 py-0">
                                    {t}
                                  </Badge>
                                ))}
                                {lead.tags.length > 3 && (
                                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-slate-400">
                                    +{lead.tags.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {listData.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                              Aucun lead trouve
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {listData.last_page > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500">
                        Page {listData.page} sur {listData.last_page} ({listData.total.toLocaleString('fr-FR')} resultats)
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={listData.page <= 1}
                          onClick={() => setListPage(listData.page - 1)}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={listData.page >= listData.last_page}
                          onClick={() => setListPage(listData.page + 1)}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default KommoLeads;
